import {type IssueAgentTask, useIssueAgentTasks} from '@github-ui/agent-sessions/hooks/use-issue-agent-tasks'
import {getLeadingVisualIconAttributes} from '@github-ui/agent-sessions/utils/get-leading-visual-icon-attributes'
import {AgentTaskSidePanel} from './AgentTaskSidePanel'
import {IssuesLoadingSkeleton} from '@github-ui/issues-loading-skeleton'
import {AnchoredOverlay, Dialog, useResponsiveValue} from '@primer/react'
import {useMemo, useRef, useState} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {HeaderAgentTasks$key} from './__generated__/HeaderAgentTasks.graphql'
import {AgentTasksToken} from './AgentTasksToken'
import styles from './HeaderAgentTasks.module.css'
import {TaskList} from './TaskList'
import {TASK_NOUN} from '../utils/constants'
import {getSummary} from '../utils/tasks-summary'
import type {LinkedIssuePullRequest} from '../utils/types'

type HeaderAgentTasksProps = {
  issueData: HeaderAgentTasks$key
  isSticky?: boolean
}

type HeaderAgentTasksInnerProps = {
  isSticky: boolean
  owner: string
  repo: string
  issueNumber: number
  linkedPullRequests?: LinkedIssuePullRequest[]
}

export function HeaderAgentTasks({isSticky = false, issueData}: HeaderAgentTasksProps) {
  const data = useFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment HeaderAgentTasks on Issue {
        number
        repository {
          nameWithOwner
        }
        agentAssignments {
          __typename
        }
        linkedPullRequestsIncludingClosed: closedByPullRequestsReferences(first: 10, includeClosedPrs: true) {
          nodes {
            number
            url
            state
            databaseId
          }
        }
      }
    `,
    issueData,
  )

  if (!data.agentAssignments.length) {
    return null
  }

  const [owner = '', repo = ''] = data.repository.nameWithOwner.split('/')

  return (
    <HeaderAgentTasksInner
      owner={owner}
      repo={repo}
      issueNumber={data.number}
      linkedPullRequests={
        data.linkedPullRequestsIncludingClosed?.nodes
          ?.filter(node => node != null)
          .map(node => ({
            number: node.number,
            url: node.url,
            state: node.state,
            databaseId: node.databaseId,
          })) ?? []
      }
      isSticky={isSticky}
    />
  )
}

export function HeaderAgentTasksInner({
  owner,
  repo,
  issueNumber,
  linkedPullRequests,
  isSticky,
}: HeaderAgentTasksInnerProps) {
  const {data, isLoading} = useIssueAgentTasks({owner, repo, issueNumber})

  const [selectedTask, setSelectedTask] = useState<IssueAgentTask | null>(null)
  const returnFocusRef = useRef<HTMLElement | null>(null)

  const tasks = useMemo(() => data?.tasks ?? [], [data?.tasks])
  const assignedAgents = useMemo(() => data?.assigned_agents ?? [], [data?.assigned_agents])
  const [isOpen, setIsOpen] = useState(false)
  const isNarrow = useResponsiveValue({narrow: true}, false) as boolean

  const {summaryText, summaryStatus} = useMemo(() => getSummary(tasks), [tasks])

  if (isLoading) {
    return isSticky ? (
      <IssuesLoadingSkeleton borderRadius="pill" height="md" width="60px" />
    ) : (
      <IssuesLoadingSkeleton borderRadius="pill" height="xl" width="80px" />
    )
  }

  if (tasks.length === 0) {
    return null
  }

  const {icon: SummaryStatusIcon, color: summaryStatusColor} = getLeadingVisualIconAttributes(
    'open',
    false,
    summaryStatus,
    false,
  )

  const narrowView = (
    <>
      <AgentTasksToken
        icon={SummaryStatusIcon}
        iconColor={summaryStatusColor}
        label={summaryText}
        onClick={() => setIsOpen(true)}
        buttonRef={returnFocusRef}
      />
      {isOpen && (
        <Dialog
          title={`Agent ${TASK_NOUN}s`}
          onClose={() => setIsOpen(false)}
          returnFocusRef={returnFocusRef}
          position={{
            narrow: 'bottom',
            regular: 'center',
          }}
        >
          <TaskList
            tasks={tasks}
            assignedAgents={assignedAgents}
            linkedPullRequests={linkedPullRequests ?? []}
            isNarrow
            onSelectTask={task => {
              setSelectedTask(task)
              setIsOpen(false)
            }}
          />
        </Dialog>
      )}
    </>
  )

  const wideView = (
    <AnchoredOverlay
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      overlayProps={{
        className: styles.overlay,
      }}
      anchorRef={returnFocusRef}
      renderAnchor={props => (
        <AgentTasksToken
          isSticky={isSticky}
          icon={SummaryStatusIcon}
          iconColor={summaryStatusColor}
          label={summaryText}
          {...props}
        />
      )}
    >
      <TaskList
        tasks={tasks}
        assignedAgents={assignedAgents}
        linkedPullRequests={linkedPullRequests ?? []}
        onSelectTask={task => {
          setSelectedTask(task)
          setIsOpen(false)
        }}
      />
    </AnchoredOverlay>
  )

  return (
    <>
      {isNarrow ? narrowView : wideView}
      {selectedTask && (
        <AgentTaskSidePanel
          task={selectedTask}
          tasks={tasks}
          open
          onSelectTask={setSelectedTask}
          onClose={() => setSelectedTask(null)}
          returnFocusRef={returnFocusRef}
        />
      )}
    </>
  )
}
